/**
 * Existing Post Matcher
 *
 * Dla mapy: matchuje nody ↔ wpisy WP w 3 poziomach:
 *  Level 1: Hash exact (canonical_question hash vs title hash)
 *  Level 2: Jaccard + entity overlap (auto-match >= 0.8, kandydat 0.6–0.8)
 *  Level 3 (tylko mode='deep'): LLM confirm via checkSemanticMatchPairs (fallback Jaccard)
 *
 * Kombinacja: wynik matchu + wp_post.quality_recommendation → final match_status:
 *  - matched_keep (keep lub borderline)
 *  - matched_refurbish (refurbish → wymaga user approval)
 *  - partial (level 3 partial_overlap)
 *  - unmatched (brak pary w scope)
 *
 * Eksporty:
 *  - matchNodesToWpPosts(mapId, options?)
 *  - getMatchingSummary(mapId)
 *  - getWpPostsForLinking(mapId, currentNodeId?, limit?)
 *  - updateNodeMatchStatus(nodeId, status, reason?)
 *  - approveRefurbish(nodeId), bulkApproveRefurbish(nodeIds), autoApproveRefurbishBelowThreshold(mapId, threshold)
 *  - determineMatchStatus(matchLevel, isPartial, postQualityRecommendation)
 */

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";
import {
  hashQuestion,
  tokenize,
  jaccardSimilarity,
  checkSemanticMatchPairsWithStats,
  type SemanticMatchPairInput,
} from "@/services/deduplicationService";
import {
  normalizeToTokens,
  normalizeToTokenSet,
  asymmetricContainment,
  stripPolishDiacritics,
  inferQueryFrameFromQuestion,
} from "@/utils/textNormalization";
import type {
  MatchMethod,
  MatchStatus,
  MatchingSummary,
  QualityRecommendation,
  WpLinkCandidate,
  WpMatchingRunMode,
} from "@/types/thematicMaps";

const PREFIX = "[WP-MATCH]";

// =============================================
// Helpers
// =============================================

function entityBonus(
  nodePrimary: string | null | undefined,
  nodeRequired: string[],
  postPlaintext: string,
): number {
  if (!postPlaintext) return 0;
  const lowerPlain = postPlaintext.toLowerCase();

  if (nodePrimary && lowerPlain.includes(nodePrimary.toLowerCase())) {
    return 1.0;
  }

  const foundRequired = nodeRequired.filter(
    (e) => e && lowerPlain.includes(e.toLowerCase()),
  ).length;
  if (foundRequired >= 2) return 0.75;
  if (foundRequired === 1) return 0.5;
  return 0;
}

export function determineMatchStatus(
  matchMethod: MatchMethod,
  isPartial: boolean,
  postQualityRecommendation: QualityRecommendation | null | undefined,
): MatchStatus {
  if (isPartial) return "partial";
  if (!postQualityRecommendation) return "matched_keep";
  switch (postQualityRecommendation) {
    case "keep":
      return "matched_keep";
    case "borderline":
      return "matched_keep"; // konserwatywnie
    case "refurbish":
      return "matched_refurbish"; // wymaga user approval
    default:
      return "matched_keep";
  }
}

// =============================================
// Match pipeline
// =============================================

export type MatchLogType = "info" | "success" | "error" | "stage";

export interface MatchingOptions {
  mode?: WpMatchingRunMode; // default 'fast'
  skipAlreadyMatched?: boolean; // default true
  onProgress?: (phase: string, current: number, total: number) => void;
  /** Granular event stream — wywolane dla kluczowych etapow (L1/L2/L3, persist). Uzywane w UI do wyswietlania logow. */
  onLog?: (type: MatchLogType, message: string, debug?: Record<string, unknown>) => void;
  triggeredBy?: string | null;
}

interface NodeRow {
  id: string;
  canonical_question: string;
  primary_entity_raw: string | null;
  required_entities_raw: unknown;
  match_status: string | null;
  title: string;
  // M9
  canonical_question_tokens_normalized: unknown;
  query_frame: string | null;
}

interface PostRow {
  id: string;
  wp_post_id: number;
  title: string;
  slug: string | null;
  wp_url: string | null;
  content_plaintext: string | null;
  content: string | null;
  quality_recommendation: QualityRecommendation | null;
  quality_score: number | null;
  word_count: number | null;
  published_at: string | null;
  // M8/M9
  title_tokens_normalized: unknown;
  first_100_tokens_normalized: unknown;
  extracted_entity: string | null;
  extracted_query_frame: string | null;
  first_100_words: string | null;
}

interface MatchingConfigRow {
  containment_auto_threshold: number;
  containment_borderline_threshold: number;
  require_entity_match: boolean;
  require_frame_match_for_auto: boolean;
  send_weak_to_llm: boolean;
  llm_batch_size: number;
  llm_concurrency: number;
  borderline_quality_as: "matched_keep" | "matched_refurbish" | "partial";
}

const DEFAULT_MATCHING_CONFIG: MatchingConfigRow = {
  containment_auto_threshold: 0.70,
  containment_borderline_threshold: 0.40,
  require_entity_match: true,
  require_frame_match_for_auto: true,
  send_weak_to_llm: false,
  llm_batch_size: 10,
  llm_concurrency: 3,
  borderline_quality_as: "matched_keep",
};

const CONFIG_SELECT =
  "containment_auto_threshold, containment_borderline_threshold, require_entity_match, require_frame_match_for_auto, send_weak_to_llm, llm_batch_size, llm_concurrency, borderline_quality_as";

async function loadMatchingConfig(mapId: string): Promise<MatchingConfigRow> {
  // Per-map config, fallback to global
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: perMap } = await (supabase as any)
    .from("matching_config")
    .select(CONFIG_SELECT)
    .eq("scope", "map")
    .eq("map_id", mapId)
    .maybeSingle();
  if (perMap) {
    return { ...DEFAULT_MATCHING_CONFIG, ...(perMap as Partial<MatchingConfigRow>) };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: global } = await (supabase as any)
    .from("matching_config")
    .select(CONFIG_SELECT)
    .eq("scope", "global")
    .maybeSingle();
  if (global) {
    return { ...DEFAULT_MATCHING_CONFIG, ...(global as Partial<MatchingConfigRow>) };
  }
  return DEFAULT_MATCHING_CONFIG;
}

function asStringArray(raw: unknown): string[] | null {
  if (Array.isArray(raw)) return raw.map((x) => String(x));
  return null;
}

function normalizeRequiredEntities(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((x) => String(x)).filter(Boolean);
  return [];
}

async function loadNodes(mapId: string, skipAlreadyMatched: boolean): Promise<NodeRow[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = (supabase as any)
    .from("thematic_nodes")
    .select(
      "id, canonical_question, primary_entity_raw, required_entities_raw, match_status, title, canonical_question_tokens_normalized, query_frame",
    )
    .eq("map_id", mapId);

  if (skipAlreadyMatched) {
    q = q.in("match_status", ["unchecked", "unmatched"]);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as unknown as NodeRow[];
}

async function loadWpPostsForMap(mapId: string): Promise<PostRow[]> {
  // Get map → website_id
  const { data: map, error: mapErr } = await supabase
    .from("thematic_maps")
    .select("id, website_id")
    .eq("id", mapId)
    .maybeSingle();

  if (mapErr || !map) throw new Error("Map not found");
  const websiteId = (map as { website_id: string | null }).website_id;
  if (!websiteId) throw new Error("Mapa nie ma przypisanej domeny (website_id)");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, wp_post_id, title, slug, wp_url, content_plaintext, content, quality_recommendation, quality_score, word_count, published_at, title_tokens_normalized, first_100_tokens_normalized, extracted_entity, extracted_query_frame, first_100_words",
    )
    .eq("website_id", websiteId)
    .not("content", "is", null);

  if (error) throw error;
  return (data || []) as unknown as PostRow[];
}

/**
 * On-the-fly backfill dla nodów które nie mają jeszcze canonical_question_tokens_normalized / query_frame.
 * Dzięki temu matcher może działać na mapach utworzonych przed M9 bez wymuszenia regeneracji.
 */
async function backfillNodeTokensIfMissing(nodes: NodeRow[]): Promise<void> {
  const needBackfill = nodes.filter(
    (n) => !Array.isArray(n.canonical_question_tokens_normalized) || !n.query_frame,
  );
  if (needBackfill.length === 0) return;

  for (const n of needBackfill) {
    const tokens = normalizeToTokens(n.canonical_question || n.title);
    const frame = inferQueryFrameFromQuestion(n.canonical_question || n.title);
    n.canonical_question_tokens_normalized = tokens;
    n.query_frame = frame;
    // Zapisz do DB (async, fire-and-forget — niekrytyczne)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("thematic_nodes")
      .update({
        canonical_question_tokens_normalized: tokens,
        query_frame: frame,
      })
      .eq("id", n.id);
  }
}

/**
 * On-the-fly backfill dla wpisów bez title_tokens_normalized / first_100_tokens_normalized
 * (np. wpisy zsyncowane PRZED M9). Nie zapisujemy do DB — tylko wypełniamy in-memory.
 */
function backfillPostTokensIfMissing(posts: PostRow[]): void {
  for (const p of posts) {
    if (!Array.isArray(p.title_tokens_normalized)) {
      p.title_tokens_normalized = normalizeToTokens(p.title);
    }
    if (!Array.isArray(p.first_100_tokens_normalized)) {
      const first100 = p.first_100_words || (p.content_plaintext || "").split(/\s+/).slice(0, 100).join(" ");
      p.first_100_tokens_normalized = normalizeToTokens(first100);
    }
  }
}

// =============================================
// Core: matchNodesToWpPosts
// =============================================

export async function matchNodesToWpPosts(
  mapId: string,
  options: MatchingOptions = {},
): Promise<{
  run_id: string;
  summary: MatchingSummary;
  duration_ms: number;
  llm_cost_usd: number;
}> {
  const mode: WpMatchingRunMode = options.mode ?? "fast";
  const skipAlreadyMatched = options.skipAlreadyMatched ?? true;
  const onLog = options.onLog;
  const t0 = Date.now();

  logPublic(PREFIX, `Matching start map=${mapId} mode=${mode}`);
  onLog?.("info", `▶ Matching start (mode=${mode}, skipAlreadyMatched=${skipAlreadyMatched})`);

  // Create run
  const { data: runRow, error: runErr } = await supabase
    .from("wp_matching_runs")
    .insert({
      map_id: mapId,
      status: "running",
      mode,
      triggered_by: options.triggeredBy ?? null,
    })
    .select("id")
    .single();

  if (runErr || !runRow) {
    throw new Error("Nie udalo sie utworzyc matching run");
  }
  const runId = (runRow as { id: string }).id;

  try {
    const [nodes, wpPosts, config] = await Promise.all([
      loadNodes(mapId, skipAlreadyMatched),
      loadWpPostsForMap(mapId),
      loadMatchingConfig(mapId),
    ]);

    logDebug(PREFIX, `Loaded nodes=${nodes.length} wpPosts=${wpPosts.length}`);
    onLog?.("stage", `  Załadowano: ${nodes.length} nodów, ${wpPosts.length} wpisów WP, config (auto=${config.containment_auto_threshold}, borderline=${config.containment_borderline_threshold})`);

    if (wpPosts.length === 0) {
      logPublic(PREFIX, `No WP posts w scope — wszystkie nody → unmatched`);
      onLog?.("error", `✗ Brak wpisów WP w scope — wszystkie ${nodes.length} nodów → unmatched`);
      await supabase
        .from("thematic_nodes")
        .update({ match_status: "unmatched", matched_at: new Date().toISOString() })
        .eq("map_id", mapId)
        .in("id", nodes.map((n) => n.id));
      return await finalizeRun(runId, mapId, t0, nodes.length, 0, 0);
    }

    // Matched pairs (one-to-one)
    type Match = {
      nodeId: string;
      postId: string;
      method: MatchMethod;
      confidence: number;
      reason: string;
      isPartial: boolean;
    };
    const matches: Match[] = [];
    const matchedNodeIds = new Set<string>();
    const matchedPostIds = new Set<string>();

    // ---------- Level 1: Hash exact ----------
    onLog?.("info", `[L1] Hash exact — sprawdzanie ${nodes.length} nodów × ${wpPosts.length} wpisów`);

    const postsByHash = new Map<string, PostRow>();
    for (const p of wpPosts) {
      postsByHash.set(hashQuestion(p.title), p);
    }

    for (const n of nodes) {
      const nHash = hashQuestion(n.canonical_question || n.title);
      const hit = postsByHash.get(nHash);
      if (hit && !matchedPostIds.has(hit.id)) {
        matches.push({
          nodeId: n.id,
          postId: hit.id,
          method: "hash",
          confidence: 1.0,
          reason: "exact_title_hash",
          isPartial: false,
        });
        matchedNodeIds.add(n.id);
        matchedPostIds.add(hit.id);
      }
    }

    options.onProgress?.("level1", nodes.length, nodes.length);
    logDebug(PREFIX, `Level 1 hash: ${matches.length} matches`);
    const level1Count = matches.length;
    onLog?.(
      "stage",
      level1Count > 0
        ? `[L1] ✓ ${level1Count} exact matches (identyczne hashe canonical_question/title)`
        : `[L1] ✓ 0 matches — brak identycznych hashy (normalne)`,
    );

    // ---------- Level 2: Asymmetric containment + entity + query_frame (SRO-aware) ----------
    // containment = |nodeTokens ∩ wpTokens| / |nodeTokens| — 1.0 gdy wszystkie tokeny pytania w artykule.
    type Candidate = {
      post: PostRow;
      containment: number;
      entityMatch: boolean;
      frameCompatible: boolean;
      auto: boolean;
    };
    const llmCandidates: Array<{ node: NodeRow; candidates: Candidate[] }> = [];
    let level2Count = 0;
    let bestScoreSeen = 0;
    const bestSeenSamples: Array<{ nodeTitle: string; postTitle: string; containment: number; entity: boolean; frame: boolean }> = [];

    const remainingNodes = nodes.filter((n) => !matchedNodeIds.has(n.id));
    onLog?.(
      "info",
      `[L2] Asymmetric containment + entity + frame — ${remainingNodes.length} nodów (auto ≥${config.containment_auto_threshold.toFixed(2)}, kandydat ≥${config.containment_borderline_threshold.toFixed(2)})`,
    );

    // On-the-fly backfill tokens (dla nodow/postow bez M9 fill)
    await backfillNodeTokensIfMissing(remainingNodes);
    backfillPostTokensIfMissing(wpPosts);

    const checkpoints = new Set<number>([
      Math.floor(remainingNodes.length * 0.25),
      Math.floor(remainingNodes.length * 0.5),
      Math.floor(remainingNodes.length * 0.75),
    ]);

    for (let i = 0; i < remainingNodes.length; i++) {
      const n = remainingNodes[i];
      const nodeTokensArr = asStringArray(n.canonical_question_tokens_normalized) || normalizeToTokens(n.canonical_question || n.title);
      const nodeTokens = new Set(nodeTokensArr);
      const nodeFrame = n.query_frame as string | null;
      const requiredEntities = normalizeRequiredEntities(n.required_entities_raw);
      const primaryEntity = n.primary_entity_raw || "";
      const primaryEntityStripped = stripPolishDiacritics(primaryEntity.toLowerCase());

      const candidates: Candidate[] = [];
      let localBest: Candidate | null = null;

      for (const p of wpPosts) {
        if (matchedPostIds.has(p.id)) continue;

        const wpTitleTokens = asStringArray(p.title_tokens_normalized) || [];
        const wpFirst100Tokens = asStringArray(p.first_100_tokens_normalized) || [];
        const wpTokens = new Set<string>([...wpTitleTokens, ...wpFirst100Tokens]);

        const containment = asymmetricContainment(nodeTokens, wpTokens);

        const plainStripped = stripPolishDiacritics((p.content_plaintext || "").toLowerCase());
        const extractedEntityStripped = stripPolishDiacritics((p.extracted_entity || "").toLowerCase());
        const entityMatch =
          (primaryEntityStripped !== "" && plainStripped.includes(primaryEntityStripped)) ||
          (primaryEntityStripped !== "" && extractedEntityStripped !== "" && extractedEntityStripped.includes(primaryEntityStripped)) ||
          requiredEntities.some((e) => {
            const es = stripPolishDiacritics(e.toLowerCase());
            return es !== "" && plainStripped.includes(es);
          });

        const postFrame = p.extracted_query_frame;
        const frameCompatible =
          !nodeFrame ||
          !postFrame ||
          nodeFrame === postFrame ||
          nodeFrame === "mixed" ||
          postFrame === "mixed";

        const cand: Candidate = { post: p, containment, entityMatch, frameCompatible, auto: false };

        if (!localBest || containment > localBest.containment) localBest = cand;

        const entityReq = !config.require_entity_match || entityMatch;
        const frameReq = !config.require_frame_match_for_auto || frameCompatible;

        if (containment >= config.containment_auto_threshold && entityReq && frameReq) {
          cand.auto = true;
          candidates.push(cand);
        } else if (containment >= config.containment_borderline_threshold || (entityMatch && containment > 0.2)) {
          candidates.push(cand);
        }
      }

      if (localBest && localBest.containment > bestScoreSeen) bestScoreSeen = localBest.containment;
      if (localBest && bestSeenSamples.length < 5 && localBest.containment > 0.3) {
        bestSeenSamples.push({
          nodeTitle: n.title,
          postTitle: localBest.post.title,
          containment: Math.round(localBest.containment * 100) / 100,
          entity: localBest.entityMatch,
          frame: localBest.frameCompatible,
        });
      }

      candidates.sort((a, b) => b.containment - a.containment);
      const best = candidates[0];

      if (best?.auto) {
        matches.push({
          nodeId: n.id,
          postId: best.post.id,
          method: "containment",
          confidence: best.containment,
          reason: `containment=${best.containment.toFixed(2)} entity=${best.entityMatch} frame=${best.frameCompatible}`,
          isPartial: false,
        });
        matchedNodeIds.add(n.id);
        matchedPostIds.add(best.post.id);
        level2Count++;
      } else if (candidates.length > 0) {
        llmCandidates.push({ node: n, candidates: candidates.slice(0, 3) });
      }

      options.onProgress?.("level2", i + 1, remainingNodes.length);

      if (checkpoints.has(i + 1)) {
        const pct = Math.round(((i + 1) / remainingNodes.length) * 100);
        onLog?.(
          "info",
          `  L2 progress ${pct}% (${i + 1}/${remainingNodes.length}) — auto=${level2Count}, borderline=${llmCandidates.length}`,
        );
      }
    }

    // Legacy imports used in Level 3 fallback checkSemanticMatchPairs
    void tokenize; void jaccardSimilarity;

    logDebug(
      PREFIX,
      `Level 2 containment: +${level2Count} auto, ${llmCandidates.length} borderline → L3`,
    );
    onLog?.(
      "stage",
      `[L2] ✓ ${level2Count} auto-matches (containment≥${config.containment_auto_threshold.toFixed(2)}), ${llmCandidates.length} borderline. Najwyższy containment: ${bestScoreSeen.toFixed(2)}`,
      {
        level2_auto: level2Count,
        level3_candidates: llmCandidates.length,
        best_containment_seen: Math.round(bestScoreSeen * 100) / 100,
        diagnostic_samples: bestSeenSamples,
        thresholds: {
          auto: config.containment_auto_threshold,
          borderline: config.containment_borderline_threshold,
        },
      },
    );

    // ---------- Level 3: LLM / fallback (tylko 'deep') ----------
    let llmCalls = 0;
    let llmTokensIn = 0;
    let llmTokensOut = 0;
    let llmCostUsd = 0;
    let level3Same = 0;
    let level3Partial = 0;
    if (mode === "deep" && llmCandidates.length > 0) {
      // Build pairs z pelnymi SRO fields (dla promptu wp_matching)
      const allPairs: SemanticMatchPairInput[] = [];

      for (const { node, candidates } of llmCandidates) {
        for (const c of candidates) {
          allPairs.push({
            nodeId: node.id,
            nodeText: node.canonical_question || node.title,
            postId: c.post.id,
            postText: `${c.post.title}. ${(c.post.content_plaintext || "").slice(0, 300)}`,
            nodeSro: {
              canonical_question: node.canonical_question || node.title,
              primary_entity: node.primary_entity_raw || "",
              required_entities: normalizeRequiredEntities(node.required_entities_raw),
              query_frame: node.query_frame,
            },
            postSro: {
              title: c.post.title,
              extracted_entity: c.post.extracted_entity,
              extracted_attribute: null, // brak w PostRow — dociagamy z pelnego load dla SRO
              extracted_query_frame: c.post.extracted_query_frame,
              first_100_words: c.post.first_100_words,
              first_paragraph_answer: null, // jak wyzej
            },
          });
        }
      }

      onLog?.(
        "info",
        `[L3] LLM wp_matching — ${allPairs.length} par w ${Math.ceil(allPairs.length / 10)} batchach`,
      );

      // Single call do checkSemanticMatchPairsWithStats — batchuje wg config
      const { results, stats } = await checkSemanticMatchPairsWithStats(allPairs, {
        onLog,
        batchSize: config.llm_batch_size,
        concurrency: config.llm_concurrency,
      });
      llmCalls = stats.llm_calls;
      llmTokensIn = stats.llm_tokens_in;
      llmTokensOut = stats.llm_tokens_out;
      llmCostUsd = stats.llm_cost_usd;

      for (const r of results) {
        if (matchedNodeIds.has(r.nodeId) || matchedPostIds.has(r.postId)) continue;

        if (r.decision === "same") {
          matches.push({
            nodeId: r.nodeId,
            postId: r.postId,
            method: "llm_confirmed",
            confidence: r.confidence,
            reason: `llm_same: ${r.explanation}`,
            isPartial: false,
          });
          matchedNodeIds.add(r.nodeId);
          matchedPostIds.add(r.postId);
          level3Same++;
        } else if (r.decision === "partial_overlap") {
          matches.push({
            nodeId: r.nodeId,
            postId: r.postId,
            method: "llm_confirmed",
            confidence: r.confidence,
            reason: `llm_partial: ${r.explanation}`,
            isPartial: true,
          });
          matchedNodeIds.add(r.nodeId);
          matchedPostIds.add(r.postId);
          level3Partial++;
        }
        // different → skip
      }

      options.onProgress?.("level3", allPairs.length, allPairs.length);

      onLog?.(
        "stage",
        stats.used_llm
          ? `[L3] ✓ LLM ${level3Same} same, ${level3Partial} partial (${llmCalls} calls, $${llmCostUsd.toFixed(4)}, ${llmTokensIn}→${llmTokensOut} tokens)`
          : `[L3] ✓ Fallback Jaccard ${level3Same} same, ${level3Partial} partial (prompt wp_matching nieaktywny)`,
        { used_llm: stats.used_llm, cost_usd: llmCostUsd, tokens_in: llmTokensIn, tokens_out: llmTokensOut },
      );
    } else if (mode === "fast" && llmCandidates.length > 0) {
      onLog?.(
        "info",
        `[L3] pominięte (mode=fast). ${llmCandidates.length} kandydatów zostanie jako unmatched. Uruchom "Matching głęboki" aby je potwierdzić.`,
      );
    }

    // ---------- Persist ----------
    onLog?.(
      "info",
      `Persist: ${matches.length} matchy → UPDATE thematic_nodes. ${nodes.length - matches.length} nodów bez pary → unmatched.`,
    );

    // Index post info dla szybkiego lookup
    const postsById = new Map<string, PostRow>(wpPosts.map((p) => [p.id, p]));

    for (const m of matches) {
      const p = postsById.get(m.postId);
      if (!p) continue;

      const finalStatus = determineMatchStatus(m.method, m.isPartial, p.quality_recommendation);

      const update: Record<string, unknown> = {
        matched_wp_post_id: p.id,
        match_status: finalStatus,
        match_confidence: Math.round(m.confidence * 100) / 100,
        match_reason: m.reason,
        match_method: m.method,
        matched_at: new Date().toISOString(),
        existing_wp_quality_score: p.quality_score,
        existing_wp_word_count: p.word_count,
      };

      if (finalStatus === "matched_refurbish") {
        // przy auto-assigned refurbish — zapisz red flags jako reason
        const { data: qualityFlags } = await supabase
          .from("wp_posts")
          .select("quality_red_flags")
          .eq("id", p.id)
          .maybeSingle();
        const flags =
          (qualityFlags as { quality_red_flags: string[] } | null)?.quality_red_flags || [];
        update.refurbish_reason = flags.join(", ");
      }

      const { error: updErr } = await supabase
        .from("thematic_nodes")
        .update(update)
        .eq("id", m.nodeId);

      if (updErr) {
        logError(PREFIX, `Update node ${m.nodeId}: ${updErr.message}`);
      }
    }

    // Mark unmatched (nodes ktore byly w scope ale nie znalazly pary)
    const unmatchedNodeIds = nodes
      .filter((n) => !matchedNodeIds.has(n.id))
      .map((n) => n.id);
    if (unmatchedNodeIds.length > 0) {
      await supabase
        .from("thematic_nodes")
        .update({ match_status: "unmatched", matched_at: new Date().toISOString() })
        .in("id", unmatchedNodeIds);
    }

    return await finalizeRun(
      runId,
      mapId,
      t0,
      nodes.length,
      wpPosts.length,
      llmCalls,
      llmTokensIn + llmTokensOut,
      llmCostUsd,
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `Matching failed: ${msg}`);
    await supabase
      .from("wp_matching_runs")
      .update({
        status: "failed",
        completed_at: new Date().toISOString(),
        error_message: msg,
      })
      .eq("id", runId);
    throw err;
  }
}

async function finalizeRun(
  runId: string,
  mapId: string,
  t0: number,
  nodesTotal: number,
  wpPostsTotal: number,
  llmCalls: number,
  llmTokensUsed: number = 0,
  llmCostUsd: number = 0,
): Promise<{
  run_id: string;
  summary: MatchingSummary;
  duration_ms: number;
  llm_cost_usd: number;
}> {
  const summary = await getMatchingSummary(mapId);
  const duration_ms = Date.now() - t0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (supabase as any)
    .from("wp_matching_runs")
    .update({
      status: "completed",
      completed_at: new Date().toISOString(),
      nodes_total: nodesTotal,
      wp_posts_total: wpPostsTotal,
      matched_keep_count: summary.matched_keep,
      matched_refurbish_count: summary.matched_refurbish,
      partial_count: summary.partial,
      unmatched_count: summary.unmatched,
      llm_calls: llmCalls,
      llm_tokens_used: llmTokensUsed,
      llm_cost_usd: llmCostUsd,
    })
    .eq("id", runId);

  logPublic(
    PREFIX,
    `Matching done ${duration_ms}ms: keep=${summary.matched_keep} refurbish=${summary.matched_refurbish} partial=${summary.partial} unmatched=${summary.unmatched}`,
  );

  // M16 auto-run: audit map po matchu (L2+L3+L5 bez LLM)
  try {
    const { auditMapFull } = await import("@/services/validation/mapAuditOrchestrator");
    options?.onLog?.("info", "▶ Auto-recompute Map Audit po matchingu (skipLLM)...");
    await auditMapFull(mapId, { skipLLM: true, onLog: options?.onLog });
  } catch (err) {
    logError(PREFIX, `Auto-recompute Map Audit failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  return { run_id: runId, summary, duration_ms, llm_cost_usd: llmCostUsd };
}

// =============================================
// getMatchingSummary
// =============================================

export async function getMatchingSummary(mapId: string): Promise<MatchingSummary> {
  const { data: nodes } = await supabase
    .from("thematic_nodes")
    .select("match_status, refurbish_approved_by_user")
    .eq("map_id", mapId);

  const rows = (nodes || []) as Array<{
    match_status: MatchStatus | null;
    refurbish_approved_by_user: boolean | null;
  }>;

  const summary: MatchingSummary = {
    total_nodes: rows.length,
    unchecked: 0,
    unmatched: 0,
    matched_keep: 0,
    matched_refurbish: 0,
    matched_refurbish_approved: 0,
    partial: 0,
    skip_manual: 0,
    wp_posts_in_scope: 0,
    wp_posts_with_quality: 0,
  };

  for (const r of rows) {
    const s = r.match_status || "unchecked";
    if (s === "unchecked") summary.unchecked++;
    else if (s === "unmatched") summary.unmatched++;
    else if (s === "matched_keep") summary.matched_keep++;
    else if (s === "matched_refurbish") {
      summary.matched_refurbish++;
      if (r.refurbish_approved_by_user) summary.matched_refurbish_approved++;
    } else if (s === "partial") summary.partial++;
    else if (s === "skip_manual") summary.skip_manual++;
  }

  // WP posts counts (opcjonalne — tylko gdy mapa ma website_id)
  const { data: map } = await supabase
    .from("thematic_maps")
    .select("website_id")
    .eq("id", mapId)
    .maybeSingle();
  const websiteId = (map as { website_id: string | null } | null)?.website_id || null;

  if (websiteId) {
    const totalRes = await supabase
      .from("wp_posts")
      .select("id", { count: "exact", head: true })
      .eq("website_id", websiteId);
    summary.wp_posts_in_scope = totalRes.count ?? 0;

    const qaRes = await supabase
      .from("wp_posts")
      .select("quality_recommendation", { count: "exact" })
      .eq("website_id", websiteId)
      .not("quality_recommendation", "is", null);
    summary.wp_posts_with_quality = qaRes.count ?? 0;

    const breakdown = { keep: 0, borderline: 0, refurbish: 0 };
    for (const row of (qaRes.data || []) as Array<{
      quality_recommendation: QualityRecommendation;
    }>) {
      breakdown[row.quality_recommendation]++;
    }
    summary.quality_breakdown = breakdown;
  }

  return summary;
}

// =============================================
// getWpPostsForLinking — pool dla Content Engine internal linking
// =============================================

export async function getWpPostsForLinking(
  mapId: string,
  currentNodeId?: string,
  limit = 10,
): Promise<WpLinkCandidate[]> {
  const { data: map } = await supabase
    .from("thematic_maps")
    .select("id, website_id")
    .eq("id", mapId)
    .maybeSingle();

  const websiteId = (map as { website_id: string | null } | null)?.website_id;
  if (!websiteId) return [];

  const { data: posts, error } = await supabase
    .from("wp_posts")
    .select(
      "id, wp_post_id, title, slug, wp_url, quality_score, content_plaintext",
    )
    .eq("website_id", websiteId)
    .not("content", "is", null)
    .limit(200);

  if (error || !posts) return [];

  let filtered = posts as Array<{
    id: string;
    wp_post_id: number;
    title: string;
    slug: string | null;
    wp_url: string | null;
    quality_score: number | null;
    content_plaintext: string | null;
  }>;

  // Exclude posty zmatchowane do current node (self-link)
  if (currentNodeId) {
    const { data: selfNode } = await supabase
      .from("thematic_nodes")
      .select("matched_wp_post_id, primary_entity_raw, required_entities_raw")
      .eq("id", currentNodeId)
      .maybeSingle();
    const sn = selfNode as {
      matched_wp_post_id: string | null;
      primary_entity_raw: string | null;
      required_entities_raw: unknown;
    } | null;

    if (sn?.matched_wp_post_id) {
      filtered = filtered.filter((p) => p.id !== sn.matched_wp_post_id);
    }

    // Score: entity overlap
    const entities = [
      sn?.primary_entity_raw,
      ...normalizeRequiredEntities(sn?.required_entities_raw),
    ]
      .filter((e): e is string => Boolean(e))
      .map((e) => e.toLowerCase());

    if (entities.length > 0) {
      const scored = filtered.map((p) => {
        const lowerPlain = (p.content_plaintext || "").toLowerCase();
        const overlaps = entities.filter((e) => lowerPlain.includes(e)).length;
        return {
          ...p,
          entity_overlap_score: overlaps / entities.length,
        };
      });
      scored.sort((a, b) => b.entity_overlap_score - a.entity_overlap_score);
      return scored.slice(0, limit).map((p) => ({
        wp_post_id: p.id,
        wp_post_internal_id: p.wp_post_id,
        title: p.title,
        slug: p.slug,
        url: p.wp_url,
        quality_score: p.quality_score ?? undefined,
        entity_overlap_score: p.entity_overlap_score,
      }));
    }
  }

  return filtered.slice(0, limit).map((p) => ({
    wp_post_id: p.id,
    wp_post_internal_id: p.wp_post_id,
    title: p.title,
    slug: p.slug,
    url: p.wp_url,
    quality_score: p.quality_score ?? undefined,
  }));
}

// =============================================
// Mutacje match_status / refurbish approval
// =============================================

export async function updateNodeMatchStatus(
  nodeId: string,
  newStatus: MatchStatus,
  reason?: string,
): Promise<void> {
  const update: Record<string, unknown> = {
    match_status: newStatus,
    matched_at: new Date().toISOString(),
  };
  if (reason) update.match_reason = reason;
  update.match_method = "manual";

  const { error } = await supabase.from("thematic_nodes").update(update).eq("id", nodeId);
  if (error) throw error;
}

export async function approveRefurbish(nodeId: string, userId?: string | null): Promise<void> {
  const { error } = await supabase
    .from("thematic_nodes")
    .update({
      refurbish_approved_by_user: true,
      refurbish_approved_at: new Date().toISOString(),
      refurbish_approved_by: userId ?? null,
    })
    .eq("id", nodeId)
    .eq("match_status", "matched_refurbish");
  if (error) throw error;
}

export async function bulkApproveRefurbish(
  nodeIds: string[],
  userId?: string | null,
): Promise<{ approved: number; errors: number }> {
  if (nodeIds.length === 0) return { approved: 0, errors: 0 };

  const { data, error } = await supabase
    .from("thematic_nodes")
    .update({
      refurbish_approved_by_user: true,
      refurbish_approved_at: new Date().toISOString(),
      refurbish_approved_by: userId ?? null,
    })
    .in("id", nodeIds)
    .eq("match_status", "matched_refurbish")
    .select("id");

  if (error) {
    logError(PREFIX, `bulkApproveRefurbish: ${error.message}`);
    return { approved: 0, errors: nodeIds.length };
  }
  const approved = (data || []).length;
  return { approved, errors: nodeIds.length - approved };
}

export async function autoApproveRefurbishBelowThreshold(
  mapId: string,
  threshold: number,
  userId?: string | null,
): Promise<{ approved: number }> {
  const { data, error } = await supabase
    .from("thematic_nodes")
    .update({
      refurbish_approved_by_user: true,
      refurbish_approved_at: new Date().toISOString(),
      refurbish_approved_by: userId ?? null,
    })
    .eq("map_id", mapId)
    .eq("match_status", "matched_refurbish")
    .lt("existing_wp_quality_score", threshold)
    .select("id");

  if (error) throw error;
  return { approved: (data || []).length };
}

// =============================================
// Recompute match_status po Quality Audit (M12)
// =============================================

/**
 * Po auditcie LLM `quality_recommendation` wpisów może się zmienić. Funkcja przechodzi
 * po wszystkich zmatchowanych nodach w mapach używających tego website i recompute'uje
 * match_status używając `determineMatchStatus` z nową rekomendacją.
 *
 * Reguły:
 *  - partial zostaje partial (LLM wp_matching decision, nie quality)
 *  - skip_manual zostaje skip_manual
 *  - matched_keep ↔ matched_refurbish przełączają się w zależności od nowej quality_recommendation
 */
export async function recomputeMatchStatusAfterAudit(
  websiteId: string,
  onLog?: (
    type: "info" | "success" | "error" | "stage",
    message: string,
    debug?: Record<string, unknown>,
  ) => void,
): Promise<{ updated: number; unchanged: number; maps_processed: number }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: maps } = await (supabase as any)
    .from("thematic_maps")
    .select("id")
    .eq("website_id", websiteId);

  const mapIds = ((maps || []) as Array<{ id: string }>).map((m) => m.id);
  if (mapIds.length === 0) {
    onLog?.("info", `Recompute: brak map powiązanych z website`);
    return { updated: 0, unchanged: 0, maps_processed: 0 };
  }

  let updated = 0;
  let unchanged = 0;

  for (const mapId of mapIds) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: nodes } = await (supabase as any)
      .from("thematic_nodes")
      .select("id, matched_wp_post_id, match_status, match_method")
      .eq("map_id", mapId)
      .not("matched_wp_post_id", "is", null);

    const nodeList = (nodes || []) as Array<{
      id: string;
      matched_wp_post_id: string;
      match_status: MatchStatus | null;
      match_method: MatchMethod | null;
    }>;

    if (nodeList.length === 0) continue;

    // Index quality_recommendation per wp_post_id (jednym zapytaniem)
    const postIds = Array.from(new Set(nodeList.map((n) => n.matched_wp_post_id)));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: posts } = await (supabase as any)
      .from("wp_posts")
      .select("id, quality_recommendation")
      .in("id", postIds);

    const qualityById = new Map<string, QualityRecommendation | null>(
      ((posts || []) as Array<{ id: string; quality_recommendation: QualityRecommendation | null }>).map(
        (p) => [p.id, p.quality_recommendation],
      ),
    );

    for (const node of nodeList) {
      // partial / skip_manual → nie ruszamy
      if (node.match_status === "partial" || node.match_status === "skip_manual") {
        unchanged++;
        continue;
      }
      const quality = qualityById.get(node.matched_wp_post_id) ?? null;
      const newStatus = determineMatchStatus(
        node.match_method ?? "containment",
        false,
        quality,
      );
      if (newStatus !== node.match_status) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: updErr } = await (supabase as any)
          .from("thematic_nodes")
          .update({
            match_status: newStatus,
            matched_at: new Date().toISOString(),
          })
          .eq("id", node.id);
        if (updErr) {
          logError(PREFIX, `Recompute update ${node.id}: ${updErr.message}`);
          unchanged++;
        } else {
          updated++;
        }
      } else {
        unchanged++;
      }
    }
  }

  logPublic(
    PREFIX,
    `Recompute done: ${updated} updated, ${unchanged} unchanged across ${mapIds.length} maps`,
  );
  onLog?.(
    "stage",
    `Recompute: ${updated} nodów zaktualizowanych, ${unchanged} bez zmian (${mapIds.length} map)`,
    { updated, unchanged, maps_processed: mapIds.length },
  );

  return { updated, unchanged, maps_processed: mapIds.length };
}

// =============================================
// Recompute cluster_fit po Matchingu (M14)
// =============================================

/**
 * Po matcherze set zmatchowanych wpisow sie zmienia. Funkcja synchronizuje cluster_fit
 * z aktualnym stanem matchera:
 *  - promote: post w matched set AND cluster_fit!='in_scope' -> UPDATE do in_scope/auto_matched
 *  - demote: post poza matched set AND cluster_fit_method='auto_matched' -> NULL
 *    (zmusza ponowna klasyfikacje LLM przy nastepnym classifyClusterFitForMap)
 *  - llm method bez zmian (user moze odpalic force reclassify)
 *  - manual_override zawsze skip
 */
export async function recomputeClusterFitAfterMatching(
  mapId: string,
  websiteId: string,
  onLog?: (
    type: "info" | "success" | "error" | "stage",
    message: string,
    debug?: Record<string, unknown>,
  ) => void,
): Promise<{ promoted_to_in_scope: number; demoted_for_reclassify: number }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: matchedNodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("matched_wp_post_id")
    .eq("map_id", mapId)
    .not("matched_wp_post_id", "is", null)
    .in("match_status", ["matched_keep", "matched_refurbish", "partial"]);

  const matchedSet = new Set(
    ((matchedNodes || []) as Array<{ matched_wp_post_id: string }>).map((r) => r.matched_wp_post_id),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: posts } = await (supabase as any)
    .from("wp_posts")
    .select("id, cluster_fit, cluster_fit_method")
    .eq("website_id", websiteId)
    .in("cluster_fit_method", ["auto_matched", "llm"]);

  const rows = (posts || []) as Array<{
    id: string;
    cluster_fit: string | null;
    cluster_fit_method: string | null;
  }>;

  let promoted = 0;
  let demoted = 0;
  const nowIso = new Date().toISOString();

  for (const p of rows) {
    const isMatched = matchedSet.has(p.id);

    if (isMatched && p.cluster_fit !== "in_scope") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("wp_posts")
        .update({
          cluster_fit: "in_scope",
          cluster_fit_confidence: 1.0,
          cluster_fit_reason: "Promoted after match — wpis zmatchowany przez SRO Matcher.",
          cluster_fit_method: "auto_matched",
          cluster_fit_classified_at: nowIso,
        })
        .eq("id", p.id);
      if (!error) promoted++;
    } else if (!isMatched && p.cluster_fit_method === "auto_matched") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("wp_posts")
        .update({
          cluster_fit: null,
          cluster_fit_confidence: null,
          cluster_fit_reason: null,
          cluster_fit_method: null,
          cluster_fit_classified_at: null,
        })
        .eq("id", p.id);
      if (!error) demoted++;
    }
  }

  logPublic(
    PREFIX,
    `Recompute cluster_fit: promoted=${promoted} demoted=${demoted} (matched_set=${matchedSet.size})`,
  );
  onLog?.(
    "stage",
    `Recompute cluster_fit: ${promoted} promoted do in_scope, ${demoted} demoted (do reklasyfikacji)`,
    { promoted, demoted, matched_count: matchedSet.size },
  );

  return { promoted_to_in_scope: promoted, demoted_for_reclassify: demoted };
}

// =============================================
// STOP-3 (2026-06-01): Dry-run matcher dla Redirect Mode (BEZ persist)
// =============================================
// Nowa exported funkcja dla picker celu mode='redirect'. Reuse L1 (hash) + L2
// (containment) logic, opcjonalny L3 LLM toggle przez options.mode='deep'.
// KRYTYCZNE: ZERO persist do DB, ZERO mutacji match_status. Tylko propozycje.

export type MatchProposalMode = "quick" | "deep";

export interface MatchProposal {
  wpPostId: string; // wp_posts.id UUID
  wpPostWpId: number; // wp_posts.wp_post_id (WP REST integer)
  wpTitle: string;
  wpUrl: string | null; // wp_posts.wp_url (LIVE URL)
  method: "hash" | "containment" | "llm_confirmed";
  confidence: number;
  reason: string;
  isPartial: boolean;
  qualityRecommendation: QualityRecommendation | null;
}

export interface MatchProposalsResult {
  nodeId: string;
  nodeTitle: string;
  nodeSlug: string;
  mode: MatchProposalMode;
  llmCostUsd: number; // 0 dla quick mode
  proposals: MatchProposal[];
}

export interface GetMatchProposalsOptions {
  mode?: MatchProposalMode; // default 'quick'
  topK?: number; // default 5
  minConfidence?: number; // default 0.4 (borderline threshold)
}

/**
 * Pobiera kandydatów do redirectu dla pojedynczego node bez zapisu do DB.
 * Używane przez panel "Istniejące Wpisy" w trybie redirect (RedirectProposalsTable, STOP-5).
 *
 * @param nodeId thematic_nodes.id
 * @param mapId thematic_maps.id
 * @param options.mode 'quick' (L1+L2, free, <1s) lub 'deep' (+L3 LLM ~$0.0025/node)
 * @param options.topK liczba zwracanych top-K propozycji (default 5)
 * @param options.minConfidence minimum confidence threshold (default 0.4)
 * @returns propozycje sortowane od najwyższej confidence
 */
export async function getMatchProposalsForNode(
  nodeId: string,
  mapId: string,
  options: GetMatchProposalsOptions = {},
): Promise<MatchProposalsResult> {
  const mode: MatchProposalMode = options.mode ?? "quick";
  const topK = options.topK ?? 5;
  const minConfidence = options.minConfidence ?? 0.4;

  // 1. Load node
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: node, error: nodeErr } = await (supabase as any)
    .from("thematic_nodes")
    .select(
      "id, title, slug, canonical_question, primary_entity_raw, required_entities_raw",
    )
    .eq("id", nodeId)
    .single();
  if (nodeErr || !node) {
    throw new Error(`Node ${nodeId} not found: ${nodeErr?.message ?? "no data"}`);
  }

  // 2. Load wp_posts dla mapy.website_id (przez join)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: mapRow } = await (supabase as any)
    .from("thematic_maps")
    .select("website_id")
    .eq("id", mapId)
    .single();
  if (!mapRow?.website_id) {
    return {
      nodeId,
      nodeTitle: node.title,
      nodeSlug: node.slug,
      mode,
      llmCostUsd: 0,
      proposals: [],
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: postsData } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, wp_post_id, title, wp_url, content_plaintext, quality_recommendation",
    )
    .eq("website_id", mapRow.website_id);

  const posts = (postsData ?? []) as Array<{
    id: string;
    wp_post_id: number;
    title: string;
    wp_url: string | null;
    content_plaintext: string | null;
    quality_recommendation: QualityRecommendation | null;
  }>;

  if (posts.length === 0) {
    return {
      nodeId,
      nodeTitle: node.title,
      nodeSlug: node.slug,
      mode,
      llmCostUsd: 0,
      proposals: [],
    };
  }

  const proposalsMap = new Map<string, MatchProposal>();

  // L1: hash exact match (canonical_question hash vs title hash)
  const nodeHash = node.canonical_question ? hashQuestion(node.canonical_question) : "";
  if (nodeHash) {
    for (const p of posts) {
      if (hashQuestion(p.title) === nodeHash) {
        proposalsMap.set(p.id, {
          wpPostId: p.id,
          wpPostWpId: p.wp_post_id,
          wpTitle: p.title,
          wpUrl: p.wp_url,
          method: "hash",
          confidence: 1.0,
          reason: "L1 hash exact match (canonical_question == title)",
          isPartial: false,
          qualityRecommendation: p.quality_recommendation,
        });
      }
    }
  }

  // L2: asymmetric containment (in-memory, deterministyczny). asymmetricContainment
  // wymaga Set<string> (NIE array) — używamy normalizeToTokenSet.
  const nodeTokens = node.canonical_question
    ? normalizeToTokenSet(node.canonical_question)
    : new Set<string>();
  const nodeRequired: string[] = Array.isArray(node.required_entities_raw)
    ? (node.required_entities_raw as string[])
    : [];
  for (const p of posts) {
    if (proposalsMap.has(p.id)) continue; // już zmatchowane przez L1
    const postTokens = normalizeToTokenSet(p.title);
    const containment = asymmetricContainment(nodeTokens, postTokens);
    const eBonus = entityBonus(
      node.primary_entity_raw,
      nodeRequired,
      p.content_plaintext ?? "",
    );
    const score = Math.min(1.0, containment * 0.7 + eBonus * 0.3);
    if (score >= minConfidence) {
      proposalsMap.set(p.id, {
        wpPostId: p.id,
        wpPostWpId: p.wp_post_id,
        wpTitle: p.title,
        wpUrl: p.wp_url,
        method: "containment",
        confidence: score,
        reason: `L2 containment=${containment.toFixed(2)} + entity_bonus=${eBonus.toFixed(2)} → ${score.toFixed(2)}`,
        isPartial: containment < 0.7,
        qualityRecommendation: p.quality_recommendation,
      });
    }
  }

  let llmCostUsd = 0;

  // L3: opcjonalny LLM confirm dla weak matches (confidence 0.4-0.7) jeśli mode='deep'
  if (mode === "deep") {
    const weak = Array.from(proposalsMap.values()).filter(
      (p) => p.confidence < 0.7 && p.method !== "hash",
    );
    if (weak.length > 0 && node.canonical_question) {
      const pairs: SemanticMatchPairInput[] = weak.map((p) => ({
        candidate_question: node.canonical_question,
        existing_question: p.wpTitle,
        candidate_id: nodeId,
        existing_id: p.wpPostId,
      }));
      try {
        const { results, stats } = await checkSemanticMatchPairsWithStats(pairs, {});
        llmCostUsd = stats?.cost_usd ?? 0;
        for (const r of results) {
          const existing = proposalsMap.get(r.existing_id);
          if (!existing) continue;
          if (r.verdict === "same") {
            existing.method = "llm_confirmed";
            existing.confidence = Math.max(existing.confidence, 0.85);
            existing.reason = `L3 LLM verdict=same (upgraded from ${existing.method})`;
            existing.isPartial = false;
          } else if (r.verdict === "partial_overlap") {
            existing.method = "llm_confirmed";
            existing.isPartial = true;
            existing.reason = `L3 LLM verdict=partial_overlap`;
          } else if (r.verdict === "different") {
            proposalsMap.delete(r.existing_id); // odrzucone przez LLM
          }
        }
      } catch (err) {
        logError(PREFIX, `L3 LLM failed (proposals only — fallback do L2 quick): ${(err as Error).message}`);
      }
    }
  }

  // Sort + top-K
  const proposals = Array.from(proposalsMap.values())
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, topK);

  return {
    nodeId,
    nodeTitle: node.title,
    nodeSlug: node.slug,
    mode,
    llmCostUsd,
    proposals,
  };
}
